import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {API_BASE_URL, AUTH_PREFIX_PATH} from "configs/AppConfig";
import {
    AUTH_TOKEN,
    EMAIL,
    FULL_NAME,
    IMAGE,
    PERMISSIONS,
    NAV_TYPE,
    USER_PK,
    CURRENCY_SYMBOL,
    CUSTOM_PRIMARY_BASE,
    CUSTOM_SECONDARY_BASE,
    APPLICATION_URL, IPAY_APPLICATION_URL, TITLE, APP_STORE_URL, SERVICES
} from "constants/AuthConstant";
import AuthService from "services/AuthService";
import ServerAPIService from "../../services/ServerAPIService";
import {message, notification} from "antd";
import CryptoJS from "crypto-js";
import {ResetPermissions} from "../../services/CheckPermissions";
import {onNavTypeChange, onHeaderNavColorChange} from 'store/slices/themeSlice';

export const initialState = {
    loading: false,
    message: "",
    showMessage: false,
    showToken: false,
    redirect: "",
    token: localStorage.getItem(AUTH_TOKEN) || null,
    fullName: localStorage.getItem(FULL_NAME) || null,
    image: localStorage.getItem(IMAGE) || null,
    email: localStorage.getItem(EMAIL) || null,
    permissions: localStorage.getItem(PERMISSIONS) || null,
    currencySymbol: localStorage.getItem(CURRENCY_SYMBOL) || null,
    role: localStorage.getItem(TITLE) || null,
    applicationUrl: localStorage.getItem(APPLICATION_URL) || null,
    appStoreUrl: localStorage.getItem(APP_STORE_URL) || null,
    iPayApplicationUrl: localStorage.getItem(IPAY_APPLICATION_URL) || null,
    services: localStorage.getItem(SERVICES) || null,
};

export const signIn = createAsyncThunk(
    `${AUTH_PREFIX_PATH}`,
    async (data, {dispatch, rejectWithValue}) => {
        const {username, password, twoFactorCode} = data;
        const SECRET_KEY = "p4X!tH8@p7RzY#3R$m6Y&k2N%bL9*Jg3G";

        try {
            const response = await AuthService.login(
                {username, password, twoFactorCode},
                `${API_BASE_URL}/auth`
            );
            if (response.data.status === "success") {
                const token = response.data.token;
                localStorage.setItem(AUTH_TOKEN, token);
                localStorage.setItem(USER_PK, response.data.userPk);
                localStorage.setItem(FULL_NAME, response.data.fullName);
                localStorage.setItem(IMAGE, response.data.image);
                localStorage.setItem(EMAIL, response.data.email);
                localStorage.setItem(NAV_TYPE, response.data.navType);
                localStorage.setItem(PERMISSIONS, CryptoJS.AES.encrypt(JSON.stringify(response.data.permissions), SECRET_KEY).toString());
                localStorage.setItem(SERVICES, CryptoJS.AES.encrypt(JSON.stringify(response.data.services), SECRET_KEY).toString());
                localStorage.setItem(CURRENCY_SYMBOL, response.data.currencySymbol);
                localStorage.setItem(CUSTOM_PRIMARY_BASE, response.data.primaryColor);
                localStorage.setItem(CUSTOM_SECONDARY_BASE, response.data.secondaryColor);
                localStorage.setItem(TITLE, response.data.role);
                localStorage.setItem(APPLICATION_URL, response.data.applicationUrl);
                localStorage.setItem(APP_STORE_URL, response.data.appStoreUrl);
                localStorage.setItem(IPAY_APPLICATION_URL, response.data.iPayApplicationUrl);

                dispatch(onNavTypeChange(response.data.navType))
                dispatch(onHeaderNavColorChange(response.data.navType === "SIDE" ? "#ffffff" : localStorage.getItem(CUSTOM_SECONDARY_BASE)))

                return token;
            } else {
                notification.error({
                    message: `Authentication Failed`,
                    description: 'Invalid username or password',
                });
                return rejectWithValue(response.data.message);
            }
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Error");
        }
    }
);

export const signUp = createAsyncThunk(
    "auth/register",
    async (data, {rejectWithValue}) => {
        const {email, password} = data;
        try {
            const response = await AuthService.register({email, password});
            const token = response.data;
            localStorage.setItem(AUTH_TOKEN, token);
            return token;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Error");
        }
    }
);

export const signOut = createAsyncThunk("auth/logout", async () => {
    ServerAPIService("DELETE", "auth", {logout: true}).then((resp) => {
        try {
            if (resp.data.status === "success") {
                localStorage.removeItem(AUTH_TOKEN);
                ResetPermissions()
            }
        } catch (e) {
            message.error("Unable To Get Information");
        }
    });
    return true;
});

export const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        authenticated: (state, action) => {
            state.loading = false;
            state.redirect = "/";
            state.token = action.payload;
        },
        showAuthMessage: (state, action) => {
            state.message = action.payload;
            state.showMessage = true;
            state.loading = false;
        },
        hideAuthMessage: (state) => {
            state.message = "";
            state.showMessage = false;
        },
        signOutSuccess: (state) => {
            state.loading = false;
            state.token = null;
            state.redirect = "/";
        },
        showLoading: (state) => {
            state.loading = true;
        },
        signInSuccess: (state, action) => {
            state.loading = false;
            state.token = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(signIn.pending, (state) => {
                state.loading = true;
            })
            .addCase(signIn.fulfilled, (state, action) => {
                state.loading = false;
                state.redirect = "/";
                state.token = action.payload;
            })
            .addCase(signIn.rejected, (state, action) => {
                if (action.payload === "twoFactorAuthentication") {
                    state.showToken = true;
                } else {
                    state.message = action.payload;
                    state.showMessage = true;
                }
                state.loading = false;
            })
            .addCase(signOut.fulfilled, (state) => {
                state.loading = false;
                state.token = null;
                state.redirect = "/";
            })
            .addCase(signOut.rejected, (state) => {
                state.loading = false;
                state.token = null;
                state.redirect = "/";
            })
            .addCase(signUp.pending, (state) => {
                state.loading = true;
            })
            .addCase(signUp.fulfilled, (state, action) => {
                state.loading = false;
                state.redirect = "/";
                state.token = action.payload;
            })
            .addCase(signUp.rejected, (state, action) => {
                state.message = action.payload;
                state.showMessage = true;
                state.loading = false;
            })
    },
});

export const {
    authenticated,
    showAuthMessage,
    hideAuthMessage,
    signOutSuccess,
    showLoading,
    signInSuccess,
} = authSlice.actions;

export default authSlice.reducer;
